import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import type { Note } from '@/types/deal';

/**
 * Anzeige eines Notiz-Inhalts (Spec §4.5, docs/tasks/notizen-richtext.md).
 *
 * Zwei Zweige:
 *   - `plain` — der Bestand. 1.039 native Notizen sind Klartext, 11,4 % davon
 *     mit getippten "-"-Listen. Sie werden als TEXT gerendert, mit
 *     erhaltenen Umbrüchen. Ein "<b>" darin ist Text, kein Befehl.
 *   - `html` — Allowlist-Markup, das ausschließlich aus dem
 *     `NoteHtmlSanitizer` stammt.
 *
 * 🔥 VERTRAUENSGRENZE: der Sanitizer sitzt auf dem SERVER, und jeder
 * Schreibweg (Editor, Import, Backfill) läuft durch ihn. Deshalb wird hier
 * nicht ein zweites Mal geparst — eine zweite Implementierung wäre eine
 * zweite Fehlerquelle, die mit der ersten auseinanderlaufen kann. Gerendert
 * wird nur, was `content_format === 'html'` trägt; alles andere bleibt Text.
 */

/** Was der Server als `content_format` liefert. */
type NoteFormat = Note['content_format'];

/**
 * Das Minimum, das Klartext und Vorschau brauchen. Bewusst schmaler als
 * `Note`: die Fall-Uebersicht arbeitet mit `latest_notes`, einer eigenen,
 * kleineren Form aus dem DealResource.
 */
export interface NoteLike {
  content: string;
  content_format?: NoteFormat;
  content_plain?: string;
}

const istHtml = (format: NoteFormat): boolean => format === 'html';

/**
 * Tag-freier Klartext einer Notiz. Kommt normalerweise fertig vom Server
 * (`content_plain`); der Rückfall greift nur für Antworten, die das Feld noch
 * nicht kennen — dann wird gestript, damit eine Vorschau nie Markup zeigt.
 */
export function noteText(note: NoteLike): string {
  if (typeof note.content_plain === 'string') {
    return note.content_plain;
  }

  if (!istHtml(note.content_format)) {
    return note.content ?? '';
  }

  return (note.content ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Einzeilige Vorschau. Nimmt IMMER den Klartext — ein `slice()` auf HTML
 * schneidet mitten in ein Tag und liefert kaputtes Markup.
 */
export function notePreview(note: NoteLike, limit: number): string {
  const text = noteText(note).replace(/\s*\n\s*/g, ' ').trim();

  return text.length > limit ? `${text.slice(0, limit)}…` : text;
}

/**
 * Klartext -> Editor-HTML. Wird gebraucht, wenn eine Bestandsnotiz zum
 * Bearbeiten geöffnet wird.
 *
 * 🔥 Getippte "-"-Zeilen bleiben WÖRTLICH stehen und werden NICHT zu <li>.
 * 11,4 % der nativen Notizen tragen solche Listen; sie beim Öffnen
 * umzudeuten hieße, den Bestand zu verändern, ohne dass jemand es angeordnet
 * hat (Spec §4.4, Vorgabe Alex).
 *
 * Spiegel von `NoteHtmlSanitizer::plainToHtml()` im Backend — bewusst
 * dieselbe Regel, damit Öffnen und Speichern nicht auseinanderlaufen.
 */
export function plainToEditorHtml(text: string): string {
  if (!text || text.trim() === '') return '';

  const escape = (s: string): string =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  return text
    .replace(/\r\n|\r/g, '\n')
    .split(/\n{2,}/)
    .map((absatz) => absatz.replace(/^\n+|\n+$/g, ''))
    .filter((absatz) => absatz.trim() !== '')
    .map((absatz) => `<p>${escape(absatz).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/**
 * Der Inhalt einer Notiz, wie ihn der Editor erwartet: HTML-Notizen wörtlich,
 * Klartext-Notizen in Absätze übersetzt.
 */
export function noteToEditorHtml(note: NoteLike): string {
  return istHtml(note.content_format) ? (note.content ?? '') : plainToEditorHtml(note.content ?? '');
}

/**
 * Styles für den HTML-Zweig. Ohne sie nimmt der CSS-Reset den Listen ihre
 * Einrückung — die Aufzählungspunkte stünden außerhalb und wären unsichtbar.
 * Die Größen bleiben unter dem Fließtext (13px), damit eine Überschrift in
 * einer Notiz nicht wie eine Seitenüberschrift wirkt.
 */
const htmlSx: SxProps<Theme> = {
  '& p': { m: 0, mb: '6px' },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { m: 0, mb: '6px', pl: '20px' },
  '& li': { mb: '2px' },
  // Design-Rollout 25.10.2026: Ueberschriften in Notizen aus den Theme-Rollen
  // (kartentitel 14/600, wertBetont 14/500) — nie groesser als der Fliesstext.
  '& h2': { typography: 'kartentitel', m: 0, mb: 2 },
  '& h3': { typography: 'wertBetont', m: 0, mb: 2 },
  '& a': { color: 'primary.main' },
  '& > *:last-child': { mb: 0 },
};

export interface NoteContentProps {
  note: Note;
  sx?: SxProps<Theme>;
}

export function NoteContent({ note, sx }: NoteContentProps) {
  const basis: SxProps<Theme> = { typography: 'wert', lineHeight: 1.5 };

  if (istHtml(note.content_format)) {
    return (
      <Box
        sx={[basis, htmlSx, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
        // Serverseitig sanitisiert (NoteHtmlSanitizer, Allowlist aus elf Tags).
        dangerouslySetInnerHTML={{ __html: note.content ?? '' }}
      />
    );
  }

  return (
    // Rolle als variant: an einer Typography wuerde `sx.typography` eine Farbe
    // des Aufrufers ueberstimmen (gemessen 25.10.2026).
    <Typography
      variant="wert"
      sx={[{ lineHeight: 1.5, whiteSpace: 'pre-line' }, ...(Array.isArray(sx) ? sx : [sx])] as SxProps<Theme>}
    >
      {note.content}
    </Typography>
  );
}

export default NoteContent;
